import React, { useState, useEffect, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import styled from 'styled-components';
import api from 'services/api';
import { validatePermission } from 'actions/index';
import { useSnackbar } from 'v3/components/Snackbar';
import Modal from 'v3/components/Modal';
import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import Loader from 'v3/components/Loader';
import theme from 'v3/theme';

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: ${theme.colors.secondary_light};
  margin-bottom: 10px;
`;

const GroupWrapper = styled.div`
  width: 100%;
  border: 1px solid ${theme.colors.secondary_light};
  border-radius: 8px;
  background: ${theme.colors.white};
  padding: 12px;
`;

const GroupHeader = styled.div`
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid ${theme.colors.secondary_light};
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  cursor: pointer;
`;

const Radio = styled.input`
  cursor: pointer;
`;

function ProfileSelection({ isOpen, onClose, selected, setSelected }) {
  const snackbar = useSnackbar();

  const [favoritesOptions, setFavoritesOptions] = useState();
  const [selection, setSelection] = useState(selected);
  const [loading, setLoading] = useState(false);

  async function fetchFavoritesOptions() {
    try {
      setLoading(true);
      const response = await api.get('v3/user/favorites-options');

      return setFavoritesOptions(response.data);
    } catch (error) {
      snackbar.show(<Text>Erro ao buscar favoritos disponíveis</Text>, {
        type: 'error',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchFavoritesOptions();
  }, []);

  useEffect(() => {
    if (selected) {
      setSelection(selected);
    }
  }, [selected]);

  async function handleSaveSelection() {
    setLoading(true);
    try {
      await api.post('v3/user/favorites', { data: selection });
      setSelected(selection);
      onClose();
      snackbar.show(<Text>Adicionado com sucesso</Text>, { type: 'success' });
    } catch (error) {
      snackbar.show(<Text>Erro ao adicionar</Text>, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function handleAddSubtitle(current, value, title) {
    const user_id = JSON.parse(localStorage.getItem('usuario')).id;

    if (selection.some((item) => item.subtitle === current)) {
      setSelection((old) => {
        return old.filter((item) => item.subtitle !== value.subtitle);
      });
    } else if (selection.length === 6) {
      snackbar.show(<Text>Selecionar no máximo 06 favoritos</Text>, {
        type: 'error',
      });
    } else {
      const selectedValue = { ...value, title, user_id };
      delete selectedValue.permission;
      setSelection((old) => [...old, selectedValue]);
    }
  }

  const groupedFavoritesOptions = useMemo(() => {
    if (!favoritesOptions?.length) return [];

    const grouped = new Map();

    favoritesOptions.forEach((option) => {
      const titleKey = option?.title?.trim?.() || '';
      if (!titleKey) return;

      const parsedItems = JSON.parse(option.parameters || '{}')?.items || [];

      if (!grouped.has(titleKey)) {
        grouped.set(titleKey, new Map());
      }

      const itemMap = grouped.get(titleKey);
      parsedItems.forEach((item) => {
        const subtitleKey = item?.subtitle?.trim?.() || '';
        if (subtitleKey && !itemMap.has(subtitleKey)) {
          itemMap.set(subtitleKey, item);
        }
      });
    });

    return Array.from(grouped.entries()).map(([title, itemsMap]) => ({
      title,
      items: Array.from(itemsMap.values()),
    }));
  }, [favoritesOptions]);

  const preparedGroups = useMemo(() => {
    return groupedFavoritesOptions
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => validatePermission(item.permission)),
      }))
      .filter((group) => group.items.length > 0);
  }, [groupedFavoritesOptions]);

  const groupedColumns = useMemo(() => {
    const left = [];
    const right = [];
    let leftWeight = 0;
    let rightWeight = 0;

    preparedGroups.forEach((group) => {
      const groupWeight = group.items.length;

      if (leftWeight === 0 && rightWeight === 0) {
        left.push(group);
        leftWeight += groupWeight;
      } else if (leftWeight < rightWeight) {
        left.push(group);
        leftWeight += groupWeight;
      } else {
        right.push(group);
        rightWeight += groupWeight;
      }
    });

    return { left, right };
  }, [preparedGroups]);

  return (
    <>
      <Modal
        show={isOpen}
        handleClose={onClose}
        size="lg"
        scrollable
        heading={
          <Text color="dark" type="header" weight="500">
            Editar favoritos
          </Text>
        }
        body={
          <>
            <Divider />
            <Row>
              <Col md={6}>
                {groupedColumns.left.map((favs, index) => {
                  return (
                    <div className="d-flex flex-column align-items-start my-2" key={`${favs.title}-${index}`}>
                      <GroupWrapper>
                        <GroupHeader>
                          <Text color="dark" type="header" weight="600">
                            {favs.title}
                          </Text>
                        </GroupHeader>
                        {favs.items.map((value, itemIndex) => {
                          return (
                            <OptionRow key={`${favs.title}-${value.subtitle}-${itemIndex}`}>
                              <Radio
                                onClick={(e) => handleAddSubtitle(e.target.value, value, favs.title)}
                                checked={selection.some((item) => item.subtitle === value.subtitle)}
                                type="radio"
                                value={value.subtitle}
                              />
                              <Text color="dark" type="regular">
                                {value.subtitle}
                              </Text>
                            </OptionRow>
                          );
                        })}
                      </GroupWrapper>
                    </div>
                  );
                })}
              </Col>
              <Col md={6}>
                {groupedColumns.right.map((favs, index) => {
                  return (
                    <div className="d-flex flex-column align-items-start my-2" key={`${favs.title}-${index}`}>
                      <GroupWrapper>
                        <GroupHeader>
                          <Text color="dark" type="header" weight="600">
                            {favs.title}
                          </Text>
                        </GroupHeader>
                        {favs.items.map((value, itemIndex) => {
                          return (
                            <OptionRow key={`${favs.title}-${value.subtitle}-${itemIndex}`}>
                              <Radio
                                onClick={(e) => handleAddSubtitle(e.target.value, value, favs.title)}
                                checked={selection.some((item) => item.subtitle === value.subtitle)}
                                type="radio"
                                value={value.subtitle}
                              />
                              <Text color="dark" type="regular">
                                {value.subtitle}
                              </Text>
                            </OptionRow>
                          );
                        })}
                      </GroupWrapper>
                    </div>
                  );
                })}
              </Col>
            </Row>
          </>
        }
        footer={
          !loading ? (
            <>
              <Button
                variant="error"
                onClick={() => {
                  onClose();
                }}
                className="mt-4"
              >
                <Text type="regular" weight="500" color="white">
                  Cancelar
                </Text>
              </Button>
              <Button onClick={() => handleSaveSelection()} className="mt-4">
                <Text type="regular" weight="500" color="white">
                  {selected.length === 0 ? 'Adicionar favorito' : 'Editar favorito'}
                </Text>
              </Button>
            </>
          ) : (
            <div className="d-flex justify-content-center mt-5">
              <Loader size="md" loading={loading} />
            </div>
          )
        }
      />
    </>
  );
}

export default ProfileSelection;
